import * as React from 'react';
import {
  IconArrowDown,
  IconArrowUp,
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconChevronsLeft,
  IconChevronsRight,
  IconLayoutColumns,
  IconSearch,
  IconSelector,
} from '@tabler/icons-react';
import {
  type ColumnDef,
  type ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
  type VisibilityState,
} from '@tanstack/react-table';

import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface DataTableProps<TData extends { id: string | number }> {
  data: TData[];
  columns: ColumnDef<TData>[];
  // Edit drawer functionality (now controlled externally)
  editingItem?: TData | null;
  onEditingItemChange?: (item: TData | null) => void;
  renderEditForm?: (item: TData, onClose: () => void) => React.ReactNode;
  // Optional submit handler for edit form
  onEditSubmit?: (item: TData) => void | Promise<void>;
  // Optional configuration
  config?: {
    enableColumnVisibility?: boolean;
    enableSorting?: boolean;
    enableFiltering?: boolean;
    defaultPageSize?: number;
    pageSizeOptions?: number[];
    editDrawerTitle?: string;
    editDrawerDescription?: string;
    searchPlaceholder?: string;
    searchableColumns?: string[]; // Which columns to search, defaults to all string columns
  };
  testIdPrefix?: string;
}

export function DataTable<TData extends { id: string | number }>({
  data,
  columns,
  editingItem,
  onEditingItemChange,
  renderEditForm,
  config = {},
  testIdPrefix = 'data-table',
}: DataTableProps<TData>) {
  const isMobile = useIsMobile();
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: config.defaultPageSize || 10,
  });
  const [globalFilter, setGlobalFilter] = React.useState('');


  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnVisibility,
      columnFilters,
      pagination,
      globalFilter,
    },
    getRowId: (row) => String(row.id),
    onSortingChange: config.enableSorting !== false ? setSorting : undefined,
    onColumnFiltersChange:
      config.enableFiltering !== false ? setColumnFilters : undefined,
    onColumnVisibilityChange:
      config.enableColumnVisibility !== false ? setColumnVisibility : undefined,
    onPaginationChange: setPagination,
    onGlobalFilterChange:
      config.enableFiltering !== false ? setGlobalFilter : undefined,
    globalFilterFn: 'includesString',
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  });

  return (
    <>
      <div className="w-full flex flex-col gap-6" data-testid={testIdPrefix}>
        <div className="flex items-center justify-between gap-4">
          {/* Search Input */}
          {config.enableFiltering !== false && (
            <div className="relative flex-1 max-w-sm">
              <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <Input
                placeholder={config.searchPlaceholder || 'Search...'}
                value={globalFilter ?? ''}
                onChange={(event) => setGlobalFilter(event.target.value)}
                className="pl-9"
                data-testid={`${testIdPrefix}-search`}
              />
            </div>
          )}

          {/* Column Visibility */}
          {config.enableColumnVisibility !== false && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid={`${testIdPrefix}-column-visibility`}
                >
                  <IconLayoutColumns />
                  <span className="hidden lg:inline">Customize Columns</span>
                  <span className="lg:hidden">Columns</span>
                  <IconChevronDown />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {table
                  .getAllColumns()
                  .filter(
                    (column) =>
                      typeof column.accessorFn !== 'undefined' &&
                      column.getCanHide()
                  )
                  .map((column) => {
                    return (
                      <DropdownMenuCheckboxItem
                        key={column.id}
                        className="capitalize"
                        checked={column.getIsVisible()}
                        onCheckedChange={(value) =>
                          column.toggleVisibility(!!value)
                        }
                      >
                        {column.id}
                      </DropdownMenuCheckboxItem>
                    );
                  })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        <div className="relative flex flex-col gap-4 overflow-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-gray-400">
          <div className="overflow-hidden rounded-lg border">
            <Table>
              <TableHeader className="bg-muted sticky top-0 z-10">
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      const canSort = header.column.getCanSort() && config.enableSorting !== false;
                      const sortedState = header.column.getIsSorted();
                      
                      return (
                        <TableHead 
                          key={header.id} 
                          colSpan={header.colSpan}
                          className={
                            header.column.columnDef.meta?.align === 'right' 
                              ? 'text-right' 
                              : header.column.columnDef.meta?.align === 'center'
                              ? 'text-center'
                              : undefined
                          }
                        >
                          {header.isPlaceholder ? null : (
                            <div
                              className={`flex items-center gap-2 ${
                                canSort 
                                  ? 'cursor-pointer select-none hover:bg-muted/50 rounded px-2 py-1 -mx-2 -my-1' 
                                  : ''
                              } ${
                                header.column.columnDef.meta?.align === 'right' 
                                  ? 'justify-end' 
                                  : header.column.columnDef.meta?.align === 'center'
                                  ? 'justify-center'
                                  : 'justify-start'
                              }`}
                              onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              {canSort && (
                                <div className="ml-auto">
                                  {sortedState === 'asc' ? (
                                    <IconArrowUp className="size-4" />
                                  ) : sortedState === 'desc' ? (
                                    <IconArrowDown className="size-4" />
                                  ) : (
                                    <IconSelector className="size-4 opacity-50" />
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      data-testid={`${testIdPrefix}-row-${row.original.id}`}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell 
                          key={cell.id}
                          className={
                            cell.column.columnDef.meta?.align === 'right' 
                              ? 'text-right' 
                              : cell.column.columnDef.meta?.align === 'center'
                              ? 'text-center'
                              : undefined
                          }
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center"
                    >
                      No results.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <div className="flex items-center justify-end px-4">
            <div className="flex items-center gap-8">
              <div className="hidden items-center gap-2 lg:flex">
                <Label htmlFor="rows-per-page" className="text-sm font-medium">
                  Rows per page
                </Label>
                <Select
                  value={`${table.getState().pagination.pageSize}`}
                  onValueChange={(value) => {
                    table.setPageSize(Number(value));
                  }}
                >
                  <SelectTrigger size="sm" className="w-20" id="rows-per-page">
                    <SelectValue
                      placeholder={table.getState().pagination.pageSize}
                    />
                  </SelectTrigger>
                  <SelectContent side="top">
                    {(config.pageSizeOptions || [10, 20, 30, 40, 50]).map(
                      (pageSize) => (
                        <SelectItem key={pageSize} value={`${pageSize}`}>
                          {pageSize}
                        </SelectItem>
                      )
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex w-fit items-center justify-center text-sm font-medium">
                Page {table.getState().pagination.pageIndex + 1} of{' '}
                {table.getPageCount()}
              </div>
              <div className="ml-auto flex items-center gap-2 lg:ml-0">
                <Button
                  variant="outline"
                  className="hidden h-8 w-8 p-0 lg:flex"
                  onClick={() => table.setPageIndex(0)}
                  disabled={!table.getCanPreviousPage()}
                  data-testid={`${testIdPrefix}-first-page`}
                >
                  <span className="sr-only">Go to first page</span>
                  <IconChevronsLeft />
                </Button>
                <Button
                  variant="outline"
                  className="size-8"
                  size="icon"
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                  data-testid={`${testIdPrefix}-previous-page`}
                >
                  <span className="sr-only">Go to previous page</span>
                  <IconChevronLeft />
                </Button>
                <Button
                  variant="outline"
                  className="size-8"
                  size="icon"
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                  data-testid={`${testIdPrefix}-next-page`}
                >
                  <span className="sr-only">Go to next page</span>
                  <IconChevronRight />
                </Button>
                <Button
                  variant="outline"
                  className="hidden size-8 lg:flex"
                  size="icon"
                  onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                  disabled={!table.getCanNextPage()}
                  data-testid={`${testIdPrefix}-last-page`}
                >
                  <span className="sr-only">Go to last page</span>
                  <IconChevronsRight />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Drawer */}
      {renderEditForm && editingItem && (
        <Drawer
          open={!!editingItem}
          onOpenChange={(open) => !open && onEditingItemChange?.(null)}
          direction={isMobile ? 'bottom' : 'right'}
        >
          <DrawerContent>
            <DrawerHeader className="gap-1">
              <DrawerTitle>{config.editDrawerTitle || 'Edit Item'}</DrawerTitle>
              <DrawerDescription>
                {config.editDrawerDescription ||
                  'Make changes to the item below'}
              </DrawerDescription>
            </DrawerHeader>
            <div className="flex flex-col gap-4 overflow-y-auto px-4 text-sm [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-gray-400">
              {renderEditForm(editingItem, () => onEditingItemChange?.(null))}
            </div>
            <DrawerFooter>
              <Button type="submit" form="edit-form">
                Submit
              </Button>
              <DrawerClose asChild>
                <Button variant="outline">Done</Button>
              </DrawerClose>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      )}
    </>
  );
}
